import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, Download, ExternalLink, Facebook, Instagram, MessageCircle,
  Hash, Calendar, Globe, MousePointerClick, Link2, LayoutGrid, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";

function fbAdLibraryUrl(source, adId) {
  // If we already have a per-ad URL (contains ?id=), use it. Otherwise
  // build it from the ad_id so legacy rows (which stored the search page
  // URL) still point at the specific ad.
  if (source && /[?&]id=\d+/.test(source)) return source;
  if (adId) return `https://www.facebook.com/ads/library/?id=${adId}`;
  return source || "";
}

function PlatformChip({ name }) {
  const p = (name || "").toLowerCase();
  const cls = "h-3.5 w-3.5";
  const icon = p.includes("facebook") ? <Facebook strokeWidth={1.5} className={cls} />
    : p.includes("instagram") ? <Instagram strokeWidth={1.5} className={cls} />
    : p.includes("messenger") ? <MessageCircle strokeWidth={1.5} className={cls} />
    : <Link2 strokeWidth={1.5} className={cls} />;
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-border bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground">
      {icon}
      <span>{name}</span>
    </span>
  );
}

function MetaRow({ Icon, label, children, testid, copy }) {
  if (!children) return null;
  return (
    <div className="grid grid-cols-[120px_1fr] sm:grid-cols-[140px_1fr] gap-3 py-3 border-b border-border/70 last:border-0" data-testid={testid}>
      <div className="flex items-start gap-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground pt-0.5">
        <Icon strokeWidth={1.5} className="h-3 w-3 mt-0.5" />
        {label}
      </div>
      <div className="text-sm flex items-start justify-between gap-2 min-w-0">
        <div className="min-w-0 break-words">{children}</div>
        {copy && typeof children === "string" && (
          <button
            onClick={() => { navigator.clipboard.writeText(children); toast.success("Copiado"); }}
            className="text-muted-foreground hover:text-foreground shrink-0"
            title="Copiar"
          >
            <Copy strokeWidth={1.5} className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdDetails() {
  const { id } = useParams();
  const { t } = useI18n();
  const [downloading, setDownloading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["download", id],
    queryFn: async () => (await api.get(`/downloads/${id}`)).data,
  });

  if (isLoading) return <Skeleton className="h-96 w-full" />;
  if (!data) return null;
  const meta = data.metadata || {};
  const adLibraryUrl = fbAdLibraryUrl(data.source_url || meta.source_url, meta.ad_id || data.ad_id);

  const forceDownload = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/downloads/${data.id}/file`, { responseType: "blob" });
      const blob = res.data;
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = data.file_name || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      toast.error("Falha ao baixar arquivo.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
          <ArrowLeft strokeWidth={1.5} className="h-4 w-4" /> {t("nav.dashboard")}
        </Link>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] gap-6">
        {/* Media panel */}
        <Card className="overflow-hidden">
          <div className="bg-secondary">
            {data.media_type === "video" ? (
              <video
                src={data.file_url}
                controls
                poster={data.thumbnail_url}
                className="w-full h-auto max-h-[80vh] block"
                data-testid="detail-video"
              />
            ) : (
              <img
                src={data.file_url}
                alt=""
                className="w-full h-auto max-h-[80vh] object-contain block bg-black/5"
                data-testid="detail-image"
              />
            )}
          </div>
          <CardContent className="p-4 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary" className="capitalize font-normal">
                {t(`history.${data.media_type}`)}
              </Badge>
              {data.resolution && <Badge variant="outline">{data.resolution}</Badge>}
              {data.country && <Badge variant="outline">{data.country}</Badge>}
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={forceDownload}
                disabled={downloading}
                data-testid="detail-download"
              >
                <Download strokeWidth={1.5} className="h-4 w-4 mr-1.5" />
                {downloading ? "Baixando…" : t("history.redownload")}
              </Button>
              {meta.destination_url && (
                <a href={meta.destination_url} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" data-testid="detail-visit-site">
                    <ExternalLink strokeWidth={1.5} className="h-4 w-4 mr-1.5" />
                    Visitar site
                  </Button>
                </a>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Metadata panel */}
        <div className="space-y-4 min-w-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="detail-page-name">
              {data.page_name || "—"}
            </h1>
            <p className="text-sm text-muted-foreground truncate">{data.file_name}</p>
          </div>

          {/* Creative copy */}
          {(meta.primary_text || meta.headline || meta.cta || meta.destination_url) && (
            <Card>
              <CardContent className="p-5 space-y-4">
                <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
                  Copy do anúncio
                </div>
                {meta.primary_text && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Texto principal</div>
                    <p className="mt-1 text-sm leading-relaxed" data-testid="detail-primary-text">
                      {meta.primary_text}
                    </p>
                  </div>
                )}
                {meta.headline && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Título</div>
                    <p className="mt-1 text-sm font-medium" data-testid="detail-headline">
                      {meta.headline}
                    </p>
                  </div>
                )}
                {(meta.destination_url || meta.cta) && (
                  <div className="flex items-center gap-3 pt-2 border-t border-border">
                    {meta.link_domain && (
                      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                        {meta.link_domain}
                      </div>
                    )}
                    {meta.cta && (
                      <div className="ml-auto">
                        <span className="inline-flex items-center rounded-md bg-secondary px-3 py-1.5 text-xs font-medium" data-testid="detail-cta">
                          {meta.cta}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Facts */}
          <Card>
            <CardContent className="p-5">
              <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground mb-2">
                Detalhes
              </div>
              <MetaRow Icon={Hash} label="Ad ID" testid="detail-adid" copy>{meta.ad_id || data.ad_id}</MetaRow>
              <MetaRow Icon={Calendar} label="Início" testid="detail-startdate">{meta.start_date}</MetaRow>
              <MetaRow Icon={Globe} label="País" testid="detail-country">{data.country || meta.country}</MetaRow>
              <MetaRow Icon={LayoutGrid} label="Formato" testid="detail-format">{meta.format}</MetaRow>
              <MetaRow Icon={MousePointerClick} label="CTA" testid="detail-cta-row">{meta.cta}</MetaRow>
              {meta.destination_url && (
                <MetaRow Icon={ExternalLink} label="Destino" testid="detail-destination">
                  <a href={meta.destination_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">
                    {meta.link_domain || meta.destination_url}
                  </a>
                </MetaRow>
              )}
              {adLibraryUrl && (
                <MetaRow Icon={Link2} label="Fonte" testid="detail-source">
                  <a href={adLibraryUrl} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground break-all text-xs">
                    Facebook Ad Library
                  </a>
                </MetaRow>
              )}
            </CardContent>
          </Card>

          {/* Platforms */}
          {(meta.platforms || []).length > 0 && (
            <Card>
              <CardContent className="p-5 space-y-3">
                <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">Plataformas</div>
                <div className="flex flex-wrap gap-1.5" data-testid="detail-platforms">
                  {meta.platforms.map((p) => <PlatformChip key={p} name={p} />)}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
